import React from 'react';
import { sortBy, uniqBy } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getModelState } from 'client/data/luckdragon/model';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { ComparatorModel } from 'client/data/models/comparator';
import {
  buildMakeModelDefaultYear,
  buildPopularStylesPath,
  buildVehiclePath,
  FeatureSpecsPaths,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { buildTrimsInventoryPath, InventoryAggregateModel } from 'client/data/models/inventory-aggregate';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { buildRecommendedTrimPath, buildReviewPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { setCriticalCss } from 'client/actions/critical-css';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getParamsWithMakes } from 'client/utils/router-params';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { hasTcoDataByYear } from 'site-modules/shared/utils/tco';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getColumnIdsString } from 'site-modules/comparator/utils/comparator';
import { decoratorParams } from 'site-modules/shared/pages/decorators/decorator-params';
import { EdmundsDefaultDecorator } from 'site-modules/shared/pages/decorators/edmunds-default';
import { TrimsComparator } from './trims-comparator';

export const pageOptions = {
  name: 'model_core_trims',
  category: 'comparator',
  component: props => <TrimsComparator {...props} params={getParamsWithMakes(props)} />,
  decorator: decoratorParams(EdmundsDefaultDecorator, { skipFloodlight: true }),
  preload: (store, props) => {
    const params = getParamsWithMakes(props);
    const { mobile } = store.getState();
    const preloader = new VehiclePreloader(store, { ...props, params }, { isSubpage: true });

    return preloader.load().then(modelState => {
      const vehicle = modelState.get(buildVehiclePath(params), VehicleModel);
      const vehicleParams = getParamsFromVehicle(vehicle);
      const isUsed = isUsedPubState(vehicle.pubStates);

      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_model_core_trims`,
          categoryName: 'comparator',
        })
      );

      // breadcrumbs
      preloader.resolve(buildMakeModelDefaultYear(vehicleParams), VehicleModel);

      preloader.resolve(buildReviewPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildRecommendedTrimPath(vehicleParams), EditorialReviewModel);
      preloader.resolve(buildPopularStylesPath(vehicleParams), VehicleModel);
      preloader.resolve(FeatureSpecsPaths.buildPartialFeaturesPath(vehicleParams), VehicleModel);
      preloader.resolve(buildTrimsInventoryPath(vehicleParams), InventoryAggregateModel);

      const hasTrimSummary = !mobile && !isUsed;
      if (hasTrimSummary) {
        preloader.resolve(buildRecommendedTrimPath(vehicleParams), EditorialReviewModel);
        preloader.resolve(buildTrimSummariesPath(vehicleParams), InventoryCoreConfigurationsModel);
      }

      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      return preloader.load().then(modelStateInstance => {
        const styles = modelStateInstance.get(buildPopularStylesPath(vehicleParams), VehicleModel);
        const comparedStyles = uniqBy(sortBy(styles, 'price.baseMSRP'), 'trim');

        comparedStyles.forEach(comparedStyle => {
          preloader.resolve(
            buildMMYSubmodelTrimPhotosPathWithParams({
              make: vehicleParams.make,
              model: vehicleParams.model,
              year: vehicleParams.year,
              submodel: comparedStyle.submodels.name,
              trim: comparedStyle.trim,
            }),
            PhotosModel
          );
        });

        const styleIdsString = getColumnIdsString(comparedStyles);
        preloader.resolve(`comparableFeatures["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`evEfficiency["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`pricing["${styleIdsString}"]`, ComparatorModel);
        preloader.resolve(`colors["${styleIdsString}"]`, ComparatorModel);
        if (hasTcoDataByYear(vehicle.year)) {
          preloader.resolve(`tco["${styleIdsString}"]`, ComparatorModel);
        }

        const hasRecommendedTrim = !!modelStateInstance.get(
          buildRecommendedTrimPath(vehicleParams),
          EditorialReviewModel
        );

        store.dispatch(setCriticalCss({ hasRecommendedTrim, isUsed }));

        return preloader.load();
      });
    });
  },
  seo: (state, props) => {
    const params = getParamsWithMakes(props);
    const modelState = getModelState(state);

    const vehicle = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);

    const vehicleName = buildDisplayVehicle({
      makeModelSubmodelYear: vehicle,
      params,
      usedPrefix: '',
      updateChevroletMakeName: true,
      disableTrademarkCharacter: true,
    });

    return {
      title: `${vehicleName} Trims Comparison [+ Chart]`,
      keywords: '',
      description: `Compare ${vehicleName} trims side by side to find the right vehicle for you. Compare prices, expert and consumer ratings, features, warranties, fuel economy, incentives and more.`,
      jsonld: [
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear: vehicle,
            params,
            defaultYear,
            pageDisplayName: 'Trims',
            urlPath: getUrlPathFromParams(params, 'trims'),
            includeLastLink: true,
          })
        ),
      ],
    };
  },
};

export default pageDefinition(pageOptions);
